import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Building2, Lightbulb, Users, Compass, MapPin, Award, HardHat } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { fetchStats } from "../lib/api";
import Counter from "./Counter";

const ICONS = {
  Building2,
  Lightbulb,
  Users,
  Compass,
  MapPin,
  Award,
  HardHat,
};

const About = () => {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState([]);

  useEffect(() => {
    fetchStats().then(setStats).catch(() => setStats([]));
  }, []);

  const values = t("about.values.items");

  return (
    <section
      id="about"
      className="bg-mayak-bg-alt text-mayak-text relative py-24 lg:py-36 overflow-hidden"
      data-testid="about-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              className="mayak-eyebrow"
              data-testid="about-eyebrow"
            >
              {t("about.eyebrow")}
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.8 }}
              className="mt-6 font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.02] text-mayak-text max-w-3xl"
              data-testid="about-title"
            >
              {t("about.title")}
            </motion.h2>

            <div className="mt-12 space-y-6 max-w-2xl text-mayak-dim leading-relaxed">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {t("about.paragraph1")}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                {t("about.paragraph2")}
              </motion.p>
            </div>

            <div className="mt-16">
              <h3 className="text-xs uppercase tracking-[0.3em] text-mayak-accent font-bold mb-8">
                {t("about.values.title")}
              </h3>
              <div className="grid sm:grid-cols-2 gap-x-12 gap-y-10">
                {Array.isArray(values) &&
                  values.map((v, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.1 }}
                      transition={{ delay: i * 0.08, duration: 0.6 }}
                      data-testid={`about-value-${i}`}
                    >
                      <div className="flex items-baseline gap-3 mb-3">
                        <span className="font-mono text-xs text-mayak-accent">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <h4 className="font-bold text-xl text-mayak-text">
                          {v.title}
                        </h4>
                      </div>
                      <p className="text-sm text-mayak-dim leading-relaxed">
                        {v.desc}
                      </p>
                    </motion.div>
                  ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pl-10 lg:border-l lg:border-mayak-border">
            <div className="sticky top-32 space-y-8">
              {stats.map((s, i) => {
                const Icon = ICONS[s.icon] || Building2;
                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ delay: i * 0.1, duration: 0.7 }}
                    className="flex items-baseline justify-between border-b border-mayak-border pb-6"
                    data-testid={`about-stat-${i}`}
                  >
                    <div className="flex items-baseline gap-5">
                      <Icon className="text-mayak-accent" size={20} />
                      <span className="text-xs uppercase tracking-[0.2em] text-mayak-dim max-w-[200px]">
                        {(s.label && s.label[lang]) || s.label?.en || ""}
                      </span>
                    </div>
                    <span className="font-black text-5xl lg:text-6xl tracking-tighter text-mayak-text">
                      <Counter value={s.value} suffix={s.suffix || ""} />
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
